import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConfigurationPanel = ({ onClose, onSave }) => {
  const [config, setConfig] = useState({
    mediaQuality: {
      video: {
        defaultQuality: '720p',
        autoUpscale: false,
        compression: 'medium',
        formats: ['mp4', 'webm']
      },
      image: {
        defaultFormat: 'webp',
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 80
      },
      audio: {
        bitrate: 128,
        format: 'mp3',
        normalize: true
      }
    },
    delivery: {
      cdn: 'auto',
      preload: 'metadata',
      lazy: true,
      caching: 'aggressive'
    },
    fallback: {
      enableFallback: true,
      fallbackVideo: '',
      fallbackImage: '/assets/images/no_image.png',
      fallbackAudio: '',
      showPlaceholder: true
    },
    validation: {
      checkDimensions: true,
      checkFileSize: true,
      maxFileSize: 100, // MB
      allowedExtensions: ['mp4', 'webm', 'jpg', 'png', 'webp', 'mp3', 'wav'],
      validateOnUpload: true
    },
    automation: {
      autoMap: false,
      matchThreshold: 0.7,
      preferExactMatch: true,
      autoResolveConflicts: false
    }
  });

  const qualityOptions = [
    { value: '480p', label: '480p (SD)' },
    { value: '720p', label: '720p (HD)' },
    { value: '1080p', label: '1080p (Full HD)' },
    { value: '1440p', label: '1440p (2K)' },
    { value: '2160p', label: '4K (Ultra HD)' }
  ];

  const compressionOptions = [
    { value: 'low', label: 'Baixa (Melhor Qualidade)' },
    { value: 'medium', label: 'Média (Balanceada)' },
    { value: 'high', label: 'Alta (Menor Arquivo)' }
  ];

  const cdnOptions = [
    { value: 'auto', label: 'Automático' },
    { value: 'cloudflare', label: 'Cloudflare' },
    { value: 'aws', label: 'AWS CloudFront' },
    { value: 'local', label: 'Servidor Local' }
  ];

  const preloadOptions = [
    { value: 'none', label: 'Nenhum' },
    { value: 'metadata', label: 'Metadados' },
    { value: 'auto', label: 'Automático' }
  ];

  const updateConfig = (path, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const keys = path?.split('.');
      let current = newConfig;
      
      for (let i = 0; i < keys?.length - 1; i++) {
        current = current?.[keys?.[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const handleSave = () => {
    onSave?.(config);
    console.log('Configurações salvas:', config);
  };

  const resetToDefault = () => {
    setConfig({
      mediaQuality: {
        video: {
          defaultQuality: '720p',
          autoUpscale: false,
          compression: 'medium',
          formats: ['mp4', 'webm']
        },
        image: {
          defaultFormat: 'webp',
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 80
        },
        audio: {
          bitrate: 128,
          format: 'mp3',
          normalize: true
        }
      },
      delivery: {
        cdn: 'auto',
        preload: 'metadata',
        lazy: true,
        caching: 'aggressive'
      },
      fallback: {
        enableFallback: true,
        fallbackVideo: '',
        fallbackImage: '/assets/images/no_image.png',
        fallbackAudio: '',
        showPlaceholder: true
      },
      validation: {
        checkDimensions: true,
        checkFileSize: true,
        maxFileSize: 100,
        allowedExtensions: ['mp4', 'webm', 'jpg', 'png', 'webp', 'mp3', 'wav'],
        validateOnUpload: true
      },
      automation: {
        autoMap: false,
        matchThreshold: 0.7,
        preferExactMatch: true,
        autoResolveConflicts: false
      }
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-tier-2 mb-6">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <Icon name="Settings" size={20} color="var(--color-secondary)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Configurações de Mapeamento</h3>
              <p className="text-sm text-muted-foreground font-caption">
                Configure qualidade de mídia, entrega e preferências de fallback
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="RotateCcw"
              onClick={resetToDefault}
            >
              Restaurar Padrão
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>
      </div>

      <div className="p-6 max-h-96 overflow-y-auto">
        <div className="space-y-8">
          {/* Media Quality Settings */}
          <div>
            <h4 className="text-md font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Settings" size={18} color="var(--color-primary)" />
              <span>Configurações de Qualidade</span>
            </h4>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Video Settings */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-foreground">Vídeo</h5>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Qualidade Padrão</label>
                    <Select
                      value={config?.mediaQuality?.video?.defaultQuality}
                      onValueChange={(value) => updateConfig('mediaQuality.video.defaultQuality', value)}
                      options={qualityOptions}
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Compressão</label>
                    <Select
                      value={config?.mediaQuality?.video?.compression}
                      onValueChange={(value) => updateConfig('mediaQuality.video.compression', value)}
                      options={compressionOptions}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={config?.mediaQuality?.video?.autoUpscale}
                      onChange={(e) => updateConfig('mediaQuality.video.autoUpscale', e?.target?.checked)}
                    />
                    <label className="text-xs text-muted-foreground">Auto-upscale</label>
                  </div>
                </div>
              </div>

              {/* Image Settings */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-foreground">Imagem</h5>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Formato Padrão</label>
                    <Select
                      value={config?.mediaQuality?.image?.defaultFormat}
                      onValueChange={(value) => updateConfig('mediaQuality.image.defaultFormat', value)}
                      options={[
                        { value: 'webp', label: 'WebP (Recomendado)' },
                        { value: 'jpg', label: 'JPEG' },
                        { value: 'png', label: 'PNG' }
                      ]}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Largura Máx</label>
                      <Input
                        type="number"
                        value={config?.mediaQuality?.image?.maxWidth}
                        onChange={(e) => updateConfig('mediaQuality.image.maxWidth', parseInt(e?.target?.value))}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Altura Máx</label>
                      <Input
                        type="number"
                        value={config?.mediaQuality?.image?.maxHeight}
                        onChange={(e) => updateConfig('mediaQuality.image.maxHeight', parseInt(e?.target?.value))}
                        className="h-8"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Qualidade ({config?.mediaQuality?.image?.quality}%)</label>
                    <input
                      type="range"
                      min="1"
                      max="100"
                      value={config?.mediaQuality?.image?.quality}
                      onChange={(e) => updateConfig('mediaQuality.image.quality', parseInt(e?.target?.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                </div>
              </div>

              {/* Audio Settings */}
              <div className="space-y-4">
                <h5 className="text-sm font-medium text-foreground">Áudio</h5>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Bitrate (kbps)</label>
                    <Input
                      type="number"
                      value={config?.mediaQuality?.audio?.bitrate}
                      onChange={(e) => updateConfig('mediaQuality.audio.bitrate', parseInt(e?.target?.value))}
                      className="h-8"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Formato</label>
                    <Select
                      value={config?.mediaQuality?.audio?.format}
                      onValueChange={(value) => updateConfig('mediaQuality.audio.format', value)}
                      options={[
                        { value: 'mp3', label: 'MP3' },
                        { value: 'aac', label: 'AAC' },
                        { value: 'ogg', label: 'OGG' }
                      ]}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={config?.mediaQuality?.audio?.normalize}
                      onChange={(e) => updateConfig('mediaQuality.audio.normalize', e?.target?.checked)}
                    />
                    <label className="text-xs text-muted-foreground">Normalizar Volume</label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Settings */}
          <div>
            <h4 className="text-md font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Truck" size={18} color="var(--color-secondary)" />
              <span>Configurações de Entrega</span>
            </h4>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">CDN Provider</label>
                  <Select
                    value={config?.delivery?.cdn}
                    onValueChange={(value) => updateConfig('delivery.cdn', value)}
                    options={cdnOptions}
                  />
                </div>
                
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Preload Strategy</label>
                  <Select
                    value={config?.delivery?.preload}
                    onValueChange={(value) => updateConfig('delivery.preload', value)}
                    options={preloadOptions}
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={config?.delivery?.lazy}
                    onChange={(e) => updateConfig('delivery.lazy', e?.target?.checked)}
                  />
                  <label className="text-xs text-muted-foreground">Lazy Loading</label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={config?.delivery?.caching === 'aggressive'}
                    onChange={(e) => updateConfig('delivery.caching', e?.target?.checked ? 'aggressive' : 'normal')}
                  />
                  <label className="text-xs text-muted-foreground">Cache Agressivo</label>
                </div>
              </div>
            </div>
          </div>

          {/* Fallback Settings */}
          <div>
            <h4 className="text-md font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Shield" size={18} color="var(--color-warning)" />
              <span>Configurações de Fallback</span>
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={config?.fallback?.enableFallback}
                  onChange={(e) => updateConfig('fallback.enableFallback', e?.target?.checked)}
                />
                <label className="text-sm text-foreground">Habilitar Conteúdo de Fallback</label>
              </div>
              
              {config?.fallback?.enableFallback && (
                <div className="ml-6 space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Vídeo Fallback</label>
                    <Input
                      placeholder="URL do vídeo padrão"
                      value={config?.fallback?.fallbackVideo}
                      onChange={(e) => updateConfig('fallback.fallbackVideo', e?.target?.value)}
                      className="h-8"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Imagem Fallback</label>
                    <Input
                      placeholder="URL da imagem padrão"
                      value={config?.fallback?.fallbackImage}
                      onChange={(e) => updateConfig('fallback.fallbackImage', e?.target?.value)}
                      className="h-8"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={config?.fallback?.showPlaceholder}
                      onChange={(e) => updateConfig('fallback.showPlaceholder', e?.target?.checked)}
                    />
                    <label className="text-xs text-muted-foreground">Mostrar Placeholder Quando Carregando</label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Automation Settings */}
          <div>
            <h4 className="text-md font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Zap" size={18} color="var(--color-success)" />
              <span>Automação</span>
            </h4>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={config?.automation?.autoMap}
                  onChange={(e) => updateConfig('automation.autoMap', e?.target?.checked)}
                />
                <label className="text-sm text-foreground">Mapeamento Automático</label>
              </div>
              
              {config?.automation?.autoMap && (
                <div className="ml-6 space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">
                      Threshold de Similaridade ({Math.round(config?.automation?.matchThreshold * 100)}%)
                    </label>
                    <input
                      type="range"
                      min="0.1"
                      max="1.0"
                      step="0.1"
                      value={config?.automation?.matchThreshold}
                      onChange={(e) => updateConfig('automation.matchThreshold', parseFloat(e?.target?.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={config?.automation?.preferExactMatch}
                      onChange={(e) => updateConfig('automation.preferExactMatch', e?.target?.checked)}
                    />
                    <label className="text-xs text-muted-foreground">Preferir Matches Exatos</label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={config?.automation?.autoResolveConflicts}
                      onChange={(e) => updateConfig('automation.autoResolveConflicts', e?.target?.checked)}
                    />
                    <label className="text-xs text-muted-foreground">Resolver Conflitos Automaticamente</label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-border">
        <div className="flex items-center justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            iconName="Save"
            onClick={handleSave}
          >
            Salvar Configurações
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;